/**
 * tableData — what `AppDataTable`'s column filters FILTER and its headers SORT.
 *
 * Pure: no React, no `ui/` barrel, no router. Split out of `AppDataTable.jsx`
 * for the reason `instituteFields.js` was split out of its page — this is the
 * half that can be wrong in a way the eye cannot see (an inclusive end date, an
 * array cell, a blank that sorts as zero), so it is the half that has to be
 * testable without dragging a component tree into the resolver.
 *
 * Re-exported from `AppDataTable.jsx`, so every call site still imports these
 * from the barrel and nothing had to move.
 */

/** Is this value doing anything? The funnel's on/off state, and the one place
 *  that question is answered — the page asks it too, for "Clear filters", and
 *  `applyColumnFilters` asks it to decide which columns are even in play.
 *
 *  ⚠️ "On, but matching everything" is a state that only ever confuses, so a
 *  blank bound or an empty set counts as OFF. */
export const isColumnFilterActive = (value) => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.values(value).some((v) => v !== "" && v != null);
  return true;
};

/** What a filter tests, per column. `render` is for the eye; this is for the
 *  predicate, and conflating them means filtering on a React element. */
const filterValueOf = (row, column) =>
  column.filterValue ? column.filterValue(row) : row?.[column.key];

const includesText = (value, query) =>
  String(value ?? "").toLowerCase().includes(String(query).toLowerCase());

/** ⚠️ `to` is INCLUSIVE to the end of its day. A date input yields midnight, so
 *  a range of 1–31 March that compared raw timestamps would silently drop
 *  everything on the 31st — the bug every naive date filter ships with. */
const withinDates = (value, { from, to }) => {
  if (!value) return false;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return false;
  if (from && t < new Date(from).getTime()) return false;
  if (to && t > new Date(to).getTime() + 86399999) return false;
  return true;
};

const withinNumbers = (value, { min, max }) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  if (min !== "" && min != null && n < Number(min)) return false;
  if (max !== "" && max != null && n > Number(max)) return false;
  return true;
};

/** A set filter is OR within a column. ⚠️ An ARRAY value (a row on several
 *  platforms, a student in several batches) matches if ANY of its entries is
 *  selected — testing the array itself would match nothing at all. */
const matchesSet = (value, selected) =>
  Array.isArray(value)
    ? value.some((v) => selected.includes(v))
    : selected.includes(value);

const matchesColumnFilter = (row, column, value) => {
  const spec = typeof column.filter === "string" ? { type: column.filter } : column.filter;
  const cell = filterValueOf(row, column);
  switch (spec?.type) {
    case "set":    return matchesSet(cell, value);
    case "number": return withinNumbers(cell, value);
    case "date":   return withinDates(cell, value);
    default:       return includesText(cell, value);
  }
};

/**
 * Narrow `rows` by the active column filters — AND across columns, whatever
 * each column's own type does within itself.
 *
 * Exported so the matching has one definition. A page writing its own would
 * disagree with the funnel's on/off state the first time an edge case came up
 * (a blank bound, an array cell, the inclusive end date above).
 */
export const applyColumnFilters = (rows, columns, filters) => {
  const active = columns.filter(
    (c) => c.filter && isColumnFilterActive(filters?.[c.key])
  );
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every((c) => matchesColumnFilter(row, c, filters[c.key]))
  );
};

/**
 * What a column ORDERS on. Falls back to the filter's accessor, then to
 * `row[key]` — so a column that already declares `filterValue` is sortable
 * without saying the same thing twice, which is the whole point: a sort reading
 * a different field from the filter beside it is the drift `applyColumnFilters`
 * was written to prevent.
 *
 * Declare `sortValue` only where the two genuinely differ — a status column
 * filtering on the enum but ordering by severity, a name column filtering on
 * "name + email + phone" but ordering on the name alone.
 */
const sortValueOf = (row, column) => {
  if (column.sortValue) return column.sortValue(row);
  if (column.filterValue) return column.filterValue(row);
  return row?.[column.key];
};

/** Blank is its own category, not a small value — see `sortRows`. */
const isBlank = (v) => v === null || v === undefined || v === "";

/**
 * Coerce a cell to something comparable, using the column's declared filter
 * type as the hint. A `date` column's accessor returns whatever the API sent —
 * an ISO string, a Date, sometimes a number — and comparing those as strings
 * puts "9 Jan" after "10 Feb".
 */
const toComparable = (value, column) => {
  const type = typeof column.filter === "string" ? column.filter : column.filter?.type;
  if (type === "date" || value instanceof Date) {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return Number(value);
  return String(value);
};

const compareValues = (x, y) => {
  if (typeof x === "number" && typeof y === "number") return x - y;
  // `numeric` so "Batch 2" files before "Batch 10", and `base` so casing does
  // not split a column into two alphabets.
  return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
};

/**
 * Order `rows` by `sort` ({ key, direction }), reading through the column's
 * accessor.
 *
 * Exported for the same reason `applyColumnFilters` is: every page that adopts
 * the header controls needs this, and fifty hand-written comparators would
 * disagree about the three things below within a month.
 *
 * ⚠️ COPIES before sorting. `rows` is the loaded array itself whenever no
 * column filter is on, and `Array.prototype.sort` is in place — sorting it
 * would reorder React state behind its back and the re-render would not happen.
 *
 * ⚠️ BLANKS SORT LAST, in both directions. They are not "smallest": a city
 * column where a third of the rows have no city would open every ascending sort
 * with a screen of em-dashes, which is the opposite of what someone who clicked
 * "sort by city" asked to see. This is Excel's rule and ag-Grid's default.
 */
export const sortRows = (rows, columns, sort) => {
  const column = sort && columns.find((c) => c.key === sort.key);
  if (!column) return rows;
  const dir = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = sortValueOf(a, column);
    const y = sortValueOf(b, column);
    const xBlank = isBlank(x);
    const yBlank = isBlank(y);
    if (xBlank || yBlank) return xBlank && yBlank ? 0 : xBlank ? 1 : -1;
    const cx = toComparable(x, column);
    const cy = toComparable(y, column);
    if (isBlank(cx) || isBlank(cy)) return isBlank(cx) && isBlank(cy) ? 0 : isBlank(cx) ? 1 : -1;
    return compareValues(cx, cy) * dir;
  });
};
